import React from 'react';
import collegeLogo from '../../assets/logo/VJ-College-logo.png';
import mottoLogo from '../../assets/images/moto.jpeg';
import { Sparkles, Compass, Layers } from 'lucide-react';


export default function AboutUsContent() {
  return (
    <div className="py-5" style={{ background: '#f8fafc' }}>
      <div className="container">
        {/* College Crest & Symbolism */}
        <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white mb-5">
          <div className="row align-items-center g-4">
            <div className="col-lg-5 text-center">
              <div className="p-3 d-inline-block rounded-4 bg-light shadow-sm" style={{ maxWidth: '320px' }}>
                <img src={collegeLogo} className="img-fluid rounded" alt="Vidyajyoti College Crest" style={{ maxHeight: '280px' }} />
              </div>
            </div>
            <div className="col-lg-7">
              <span className="badge px-3 py-1 rounded-pill mb-2" style={{ background: '#1a2b56', color: '#fff' }}>Institutional Emblem</span>
              <h2 className="fw-bold mb-3" style={{ color: '#1a2b56', fontFamily: "'Outfit', sans-serif" }}>
                The College Crest &amp; Symbolism
              </h2>
              <p className="text-muted" style={{ lineHeight: '1.8', textAlign: 'justify' }}>
                The Vidyajyoti Crest carries profound theological, interdisciplinary, and inter-religious significance. The Cross within it, shaped by an <strong>eye</strong> and a <strong>flame</strong>, symbolizes divine illumination and Christian wisdom. The star inside the eye represents <strong>Mary, Seat of Wisdom</strong>, the institution’s heavenly Patroness.
              </p>
              <div className="p-3 rounded-3 mt-3" style={{ background: 'rgba(26,43,86,0.04)', borderLeft: '4px solid #1a2b56' }}>
                <p className="small mb-0 text-dark" style={{ lineHeight: '1.6' }}>
                  The crest embodies the college motto: <em>&ldquo;tam eva bhantam anubhati sarvam&rdquo;</em> (Katha Upanishad 5:15) — &ldquo;Everything reflects the one who alone shines.&rdquo; God (the flame) is the ultimate source of light illuminating human consciousness (the eye) as it searches for truth and dispels ignorance.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Institutional Motto Card */}
        <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 mb-5" style={{ background: 'linear-gradient(135deg, #79031d 0%, #9e0e2d 100%)', color: '#fff' }}>
          <div className="row align-items-center g-4">
            <div className="col-lg-7">
              <span className="badge px-3 py-1 rounded-pill mb-2" style={{ background: 'rgba(255,255,255,0.2)', color: '#fff' }}>Sacred Motto</span>
              <h2 className="fw-bold text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Our Motto: <em>tam eva bhāntam anubhāti sarvam</em>
              </h2>
              <h5 className="text-warning mb-3">
                &ldquo;Everything reflects the One who alone shines.&rdquo;
              </h5>
              <p className="text-light opacity-90 mb-0" style={{ lineHeight: '1.8', fontSize: '1.02rem', textAlign: 'justify' }}>
                Derived from the <em>Katha Upanishad (5:15)</em>, our motto affirms Vidyajyoti’s perpetual commitment to seek the eternal Divine Light that is Life, to contemplate it in prayer and scholarship, and to let it radiate through transformative human insight and prophetic ecclesial service.
              </p>
            </div>
            <div className="col-lg-5 text-center">
              <div className="p-3 d-inline-block rounded-4 bg-white shadow" style={{ maxWidth: '320px' }}>
                <img src={mottoLogo} className="img-fluid rounded" alt="Vidyajyoti College Motto" style={{ maxHeight: '240px' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Contextual-Inclusive Methodology */}
        <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white">
          <div className="text-center mx-auto mb-5" style={{ maxWidth: '750px' }}>
            <span className="badge px-3 py-1 rounded-pill mb-2" style={{ background: 'rgba(198,146,20,0.15)', color: '#8a6200', fontWeight: 600 }}>Theological Pedagogy</span>
            <h2 className="fw-bold" style={{ color: '#1a2b56', fontFamily: "'Outfit', sans-serif" }}>
              Contextual &amp; Inclusive Methodology
            </h2>
            <p className="text-muted" style={{ lineHeight: '1.7' }}>
              Vidyajyoti’s theological method begins from South Asian subaltern realities, opening outward to universal ecclesial questions through an organic dialectic of faith and life.
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="p-4 rounded-4 h-100 border" style={{ background: '#f8fafc', borderTop: '4px solid #1a2b56' }}>
                <div className="d-inline-flex p-3 rounded-circle mb-3" style={{ background: 'rgba(26,43,86,0.08)', color: '#1a2b56' }}>
                  <Layers size={24} />
                </div>
                <h5 className="fw-bold mb-2" style={{ color: '#1a2b56' }}>Three-Tier Curriculum</h5>
                <p className="text-muted small mb-0" style={{ lineHeight: '1.6' }}>
                  Progresses holistically through three foundational themes: God within History, God in Jesus Christ &amp; Church, and Christians in the World.
                </p>
              </div>
            </div>

            <div className="col-md-4">
              <div className="p-4 rounded-4 h-100 border" style={{ background: '#f8fafc', borderTop: '4px solid #c69214' }}>
                <div className="d-inline-flex p-3 rounded-circle mb-3" style={{ background: 'rgba(198,146,20,0.15)', color: '#c69214' }}>
                  <Compass size={24} />
                </div>
                <h5 className="fw-bold mb-2" style={{ color: '#1a2b56' }}>Apostolic Praxis</h5>
                <p className="text-muted small mb-0" style={{ lineHeight: '1.6' }}>
                  Fieldwork with urban poor, refugees, and marginalized groups serves as an essential locus theologicus for critical hermeneutics.
                </p>
              </div>
            </div>

            <div className="col-md-4">
              <div className="p-4 rounded-4 h-100 border" style={{ background: '#f8fafc', borderTop: '4px solid #79031d' }}>
                <div className="d-inline-flex p-3 rounded-circle mb-3" style={{ background: 'rgba(121,3,29,0.08)', color: '#79031d' }}>
                  <Sparkles size={24} />
                </div>
                <h5 className="fw-bold mb-2" style={{ color: '#1a2b56' }}>Holistic Transformation</h5>
                <p className="text-muted small mb-0" style={{ lineHeight: '1.6' }}>
                  Aims at cognitive, spiritual, and structural transformation, equipping pastors and theologians for prophetic engagement.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
